import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, DollarSign, Target, Calendar, Download, Search, 
  ChevronDown, ChevronUp, AlertCircle, CheckCircle, Clock,
  Award, TrendingUp, FileText, Ban, Edit, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Commission Tiers
const COMMISSION_TIERS = {
  bronze: { name: 'Bronze', min: 0, max: 9, rate: 8, color: '#CD7F32' },
  silver: { name: 'Silver', min: 10, max: 19, rate: 10, color: '#C0C0C0' },
  gold: { name: 'Gold', min: 20, max: Infinity, rate: 12, color: '#D4AF37' },
};

const MINIMUM_PAYOUT = 40;

const AdminReferrals = () => {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [expiredEarnings, setExpiredEarnings] = useState([]);
  const [summary, setSummary] = useState({
    totalAffiliates: 0,
    totalSales: 0,
    totalCommission: 0,
    eligibleForPayout: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all referrals/affiliates
      const referralsRes = await axios.get(`${API}/admin/referrals`);
      const affiliatesData = referralsRes.data || [];
      
      // Process affiliate data
      const processedAffiliates = affiliatesData.map(aff => {
        const monthlySales = aff.monthly_sales || 0;
        const tier = getTier(monthlySales);
        const commission = monthlySales * tier.rate;
        const meetsThreshold = commission >= MINIMUM_PAYOUT;
        
        return {
          ...aff,
          tier: tier.name,
          tierColor: tier.color,
          commissionRate: tier.rate,
          monthlyCommission: commission,
          meetsThreshold,
          payoutStatus: meetsThreshold ? 'eligible' : commission > 0 ? 'below_threshold' : 'no_sales',
        };
      });

      setAffiliates(processedAffiliates);

      // Calculate summary
      const totalAffiliates = processedAffiliates.length;
      const totalSales = processedAffiliates.reduce((sum, a) => sum + (a.monthly_sales || 0), 0);
      const totalCommission = processedAffiliates.reduce((sum, a) => sum + a.monthlyCommission, 0);
      const eligibleForPayout = processedAffiliates.filter(a => a.meetsThreshold).length;

      setSummary({ totalAffiliates, totalSales, totalCommission, eligibleForPayout });

      // Fetch expired earnings log
      const expiredRes = await axios.get(`${API}/admin/referrals/expired`);
      setExpiredEarnings(expiredRes.data || []);

    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      // Use mock data if API not available
      setAffiliates([]);
      setSummary({ totalAffiliates: 0, totalSales: 0, totalCommission: 0, eligibleForPayout: 0 });
    } finally {
      setLoading(false);
    }
  };

  const getTier = (sales) => {
    if (sales >= 20) return COMMISSION_TIERS.gold;
    if (sales >= 10) return COMMISSION_TIERS.silver;
    return COMMISSION_TIERS.bronze;
  };

  const handleMarkPaid = async (affiliateId) => {
    try {
      await axios.put(`${API}/admin/referrals/${affiliateId}/payout`, { status: 'paid' });
      toast.success('Marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update payout status');
    }
  };

  const handleSuspend = async (affiliateId) => {
    if (!window.confirm('Are you sure you want to suspend this affiliate?')) return;
    try {
      await axios.put(`${API}/admin/referrals/${affiliateId}/suspend`);
      toast.success('Affiliate suspended');
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend affiliate');
    }
  };

  const handleSaveNote = async (affiliateId) => {
    try {
      await axios.put(`${API}/admin/referrals/${affiliateId}/note`, { note: noteText });
      toast.success('Note saved');
      setEditingNote(null);
      setNoteText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const exportCSV = () => {
    const eligibleAffiliates = affiliates.filter(a => a.meetsThreshold);
    if (eligibleAffiliates.length === 0) {
      toast.error('No affiliates eligible for payout');
      return;
    }

    const headers = ['Name', 'Email', 'Referral Code', 'Sales', 'Tier', 'Commission Rate', 'Total Commission', 'Bank Details'];
    const rows = eligibleAffiliates.map(a => [
      a.user_name || 'N/A',
      a.user_email || 'N/A',
      a.referral_code,
      a.monthly_sales,
      a.tier,
      `£${a.commissionRate}`,
      `£${a.monthlyCommission.toFixed(2)}`,
      a.bank_details_verified ? 'Verified' : 'Missing'
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payout-ready-affiliates-${selectedMonth}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  const filteredAffiliates = affiliates.filter(a => 
    (a.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     a.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     a.referral_code?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadge = (status) => {
    const styles = {
      eligible: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', icon: CheckCircle, text: 'Eligible' },
      below_threshold: { bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308', icon: AlertCircle, text: 'Below £40' },
      no_sales: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', icon: Clock, text: 'No Sales' },
      paid: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', icon: CheckCircle, text: 'Paid' },
      expired: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', icon: AlertCircle, text: 'Expired' },
    };
    const style = styles[status] || styles.no_sales;
    const Icon = style.icon;
    return (
      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: style.bg, color: style.color }}>
        <Icon size={12} />
        {style.text}
      </span>
    );
  };

  const getNextPayoutDate = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading referral data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="admin-referrals-page">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#ffffff' }}>Referral Tracking</h1>
            <p className="text-muted-foreground mt-1">Monitor affiliate performance and payouts</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin')}>
            ← Back to Admin
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="glass-panel rounded-xl p-5 border">
            <div className="flex items-center justify-between mb-2">
              <Users size={20} style={{ color: '#D4AF37' }} />
              <span className="text-2xl font-bold">{summary.totalAffiliates}</span>
            </div>
            <p className="text-xs text-muted-foreground">Active Affiliates</p>
          </div>
          <div className="glass-panel rounded-xl p-5 border">
            <div className="flex items-center justify-between mb-2">
              <Target size={20} style={{ color: '#D4AF37' }} />
              <span className="text-2xl font-bold">{summary.totalSales}</span>
            </div>
            <p className="text-xs text-muted-foreground">Sales This Month</p>
          </div>
          <div className="glass-panel rounded-xl p-5 border">
            <div className="flex items-center justify-between mb-2">
              <DollarSign size={20} style={{ color: '#D4AF37' }} />
              <span className="text-2xl font-bold">£{summary.totalCommission.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground">Total Commission</p>
          </div>
          <div className="glass-panel rounded-xl p-5 border">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle size={20} style={{ color: '#22c55e' }} />
              <span className="text-2xl font-bold">{summary.eligibleForPayout}</span>
            </div>
            <p className="text-xs text-muted-foreground">Eligible for Payout</p>
          </div>
          <div className="glass-panel rounded-xl p-5 border">
            <div className="flex items-center justify-between mb-2">
              <Calendar size={20} style={{ color: '#D4AF37' }} />
              <span className="text-sm font-bold">{getNextPayoutDate()}</span>
            </div>
            <p className="text-xs text-muted-foreground">Next Payout Date</p>
          </div>
        </div>

        {/* Filters & Actions */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-48"
          />
          <Button onClick={exportCSV} variant="outline" className="flex items-center gap-2">
            <Download size={16} />
            Export Payout CSV
          </Button>
        </div>

        {/* Commission Tiers Reference */}
        <div className="glass-panel rounded-xl p-4 border mb-6">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <Award size={18} style={{ color: '#D4AF37' }} />
            Commission Tiers (30-Day DJ Course Only)
          </h3>
          <div className="flex flex-wrap gap-4">
            {Object.values(COMMISSION_TIERS).map((tier) => (
              <div key={tier.name} className="flex items-center gap-2 px-3 py-1 rounded-full border" style={{ borderColor: tier.color }}>
                <div className="w-3 h-3 rounded-full" style={{ background: tier.color }} />
                <span className="text-sm font-semibold" style={{ color: tier.color }}>{tier.name}</span>
                <span className="text-xs text-muted-foreground">({tier.min}-{tier.max === Infinity ? '20+' : tier.max} sales)</span>
                <span className="text-sm font-bold">£{tier.rate}/sale</span>
              </div>
            ))}
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border border-red-500/30">
              <AlertCircle size={14} className="text-red-500" />
              <span className="text-sm text-red-400">Min Payout: £{MINIMUM_PAYOUT}</span>
            </div>
          </div>
        </div>

        {/* Affiliates Table */}
        <div className="glass-panel rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ background: 'rgba(212, 175, 55, 0.05)' }}>
                  <th className="text-left p-4 text-sm font-semibold">Affiliate</th>
                  <th className="text-left p-4 text-sm font-semibold">Code</th>
                  <th className="text-center p-4 text-sm font-semibold">Sales</th>
                  <th className="text-center p-4 text-sm font-semibold">Tier</th>
                  <th className="text-center p-4 text-sm font-semibold">Rate</th>
                  <th className="text-center p-4 text-sm font-semibold">Commission</th>
                  <th className="text-center p-4 text-sm font-semibold">Status</th>
                  <th className="text-center p-4 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAffiliates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center p-8 text-muted-foreground">
                      No affiliates found
                    </td>
                  </tr>
                ) : (
                  filteredAffiliates.map((affiliate) => (
                    <>
                      <tr 
                        key={affiliate.id} 
                        className="border-b hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => setExpandedAffiliate(expandedAffiliate === affiliate.id ? null : affiliate.id)}
                      >
                        <td className="p-4">
                          <div>
                            <p className="font-semibold" style={{ color: '#ffffff' }}>{affiliate.user_name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">{affiliate.user_email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <code className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                            {affiliate.referral_code}
                          </code>
                        </td>
                        <td className="p-4 text-center">
                          <span className="font-mono font-bold">{affiliate.monthly_sales || 0}</span>
                        </td>
                        <td className="p-4 text-center">
                          <span className="font-semibold" style={{ color: affiliate.tierColor }}>{affiliate.tier}</span>
                        </td>
                        <td className="p-4 text-center">
                          <span className="font-mono">£{affiliate.commissionRate}</span>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`font-mono font-bold ${affiliate.meetsThreshold ? 'text-green-500' : ''}`}>
                            £{affiliate.monthlyCommission.toFixed(2)}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          {getStatusBadge(affiliate.payoutStatus)}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setExpandedAffiliate(affiliate.id); }}>
                              <Eye size={14} />
                            </Button>
                            {affiliate.meetsThreshold && (
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleMarkPaid(affiliate.id); }} style={{ color: '#22c55e' }}>
                                <CheckCircle size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedAffiliate === affiliate.id && (
                        <tr>
                          <td colSpan={8} className="p-4" style={{ background: 'rgba(255,255,255,0.02)' }}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <h4 className="font-semibold mb-2 text-sm">Details</h4>
                                <p className="text-xs text-muted-foreground">ID: {affiliate.id}</p>
                                <p className="text-xs text-muted-foreground">Total Sales (All Time): {affiliate.total_sales || 0}</p>
                                <p className="text-xs text-muted-foreground">Total Earnings (All Time): £{(affiliate.total_earnings || 0).toFixed(2)}</p>
                                <p className="text-xs text-muted-foreground">Bank Details: {affiliate.bank_details_verified ? '✓ Verified' : '✗ Missing'}</p>
                              </div>
                              <div>
                                <h4 className="font-semibold mb-2 text-sm">Admin Notes</h4>
                                {editingNote === affiliate.id ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      value={noteText}
                                      onChange={(e) => setNoteText(e.target.value)}
                                      placeholder="Add admin note..."
                                      rows={2}
                                    />
                                    <div className="flex gap-2">
                                      <Button size="sm" onClick={() => handleSaveNote(affiliate.id)}>Save</Button>
                                      <Button size="sm" variant="outline" onClick={() => setEditingNote(null)}>Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-2">{affiliate.admin_note || 'No notes'}</p>
                                    <Button size="sm" variant="outline" onClick={() => { setEditingNote(affiliate.id); setNoteText(affiliate.admin_note || ''); }}>
                                      <Edit size={12} className="mr-1" /> Edit Note
                                    </Button>
                                  </div>
                                )}
                              </div>
                              <div>
                                <h4 className="font-semibold mb-2 text-sm">Actions</h4>
                                <div className="space-y-2">
                                  {affiliate.meetsThreshold && (
                                    <Button size="sm" className="w-full" onClick={() => handleMarkPaid(affiliate.id)} style={{ background: '#22c55e' }}>
                                      <CheckCircle size={14} className="mr-1" /> Mark as Paid
                                    </Button>
                                  )}
                                  <Button size="sm" variant="outline" className="w-full" onClick={() => handleSuspend(affiliate.id)} style={{ color: '#ef4444', borderColor: '#ef4444' }}>
                                    <Ban size={14} className="mr-1" /> Suspend Affiliate
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Expired Earnings Log */}
        <div className="mt-8 glass-panel rounded-xl border p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <FileText size={18} style={{ color: '#ef4444' }} />
            Expired Earnings Log (Audit Trail)
          </h3>
          {expiredEarnings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No expired earnings recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Affiliate</th>
                    <th className="text-left p-2">Month</th>
                    <th className="text-right p-2">Amount Expired</th>
                    <th className="text-left p-2">Reason</th>
                    <th className="text-left p-2">Expiry Date</th>
                  </tr>
                </thead>
                <tbody>
                  {expiredEarnings.map((record, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2">{record.affiliate_name}</td>
                      <td className="p-2">{record.month}</td>
                      <td className="p-2 text-right text-red-400">£{record.amount.toFixed(2)}</td>
                      <td className="p-2 text-muted-foreground">{record.reason}</td>
                      <td className="p-2 text-muted-foreground">{record.expiry_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReferrals;
